import { useState, type Dispatch, type SetStateAction, useCallback, useEffect } from "react"
import StepperScoring from "../stepper/stepper-scoring"
import type { FormularioRespuestaDTO } from "app/analista/scoring/adapters/dto/resultado-dto"
import type { DetalleNegocioDTO } from "app/analista/adapters/dto/detalle-solicitud-dto"
import { ConfigVariables } from "adapters/ports/config-variables"
import { SolicitudesHubspotDTO } from "app/analista/adapters/dto/solicitudes-hubspot-dto"

interface ExtendedDetailDTO extends DetalleNegocioDTO {
  scoringResult?: FormularioRespuestaDTO
  approved?: boolean
  score?: number
  resume?: Record<string, number | boolean | string>
}

interface CardScoringProps {
  solicitud: ExtendedDetailDTO | null
  setSolicitud: Dispatch<SetStateAction<ExtendedDetailDTO | null>>
  solicitudTabla: SolicitudesHubspotDTO | null
}

const truncateToTwoDecimals = (number: number) => {
  return Math.trunc(number * 100) / 100
}

interface ScoringInfo {
  colorClass: string;
  text: string;
  background: string;
}

const getScoringInfo = (score: number): ScoringInfo => {
  if (score < 3) {
    return {
      colorClass: "text-[#63171B]",
      text: "Riesgo Alto",
      background: ConfigVariables.BACKGROUND_RED_SCORING,
    };
  } else if (score < 4) {
    return {
      colorClass: "text-[#7B341E]",
      text: "Riesgo Medio",
      background: ConfigVariables.BACKGROUND_YELLOW_SCORING,
    };
  } else {
    return {
      colorClass: "text-[#1C4532]",
      text: "Riesgo Bajo",
      background: ConfigVariables.BACKGROUND_GREEN_SCORING,
    };
  }
};


export default function CardScoring({ solicitud, setSolicitud, solicitudTabla }: CardScoringProps) {
  const [allStepsComplete, setAllStepsComplete] = useState(false)
  const [isCalculating, setIsCalculating] = useState(false)
  const [fieldsEditable, setFieldsEditable] = useState(true)

  const isReadyForStudy = solicitudTabla?.dealstage === "24016550"

  useEffect(() => {
    if (solicitud?.scoringResult) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [solicitud?.scoringResult])

  const handleCompletionChange = useCallback((isComplete: boolean) => {
    setAllStepsComplete(isComplete)
  }, [])

  const handleCalculate = useCallback(() => {
    if (!allStepsComplete || isReadyForStudy) return
    setIsCalculating(true)
    setFieldsEditable(false)
    setIsCalculating(false)
  }, [allStepsComplete, isReadyForStudy])

  const handleEditForm = useCallback(() => {
    if (isReadyForStudy) return
    setFieldsEditable(true);
    if (solicitud) {
      setSolicitud({
        ...solicitud,
        scoringResult: undefined
      });
    }
  }, [solicitud, setSolicitud, isReadyForStudy]);

  const renderColorCodedScore = () => {
    if (!solicitud?.scoringResult) 
  return null

  const score = solicitud.scoringResult.score
  const {colorClass, text, background} = getScoringInfo(score)

  return (
    <div className="mt-6">
      <div className="w-full h-24 px-8 rounded-[20px] bg-cover bg-center flex flex-col justify-center" style={{ backgroundImage: `url(${background})`, backgroundSize: "cover" }}>
          <div className="flex justify-between items-center">
              <div className={`text-xl font-normal ${colorClass}`}>Resultado scoring</div>
              <div className="flex items-center">
                  <div className="text-center">
                      <span className={`font-bold text-5xl ${colorClass}`}>{truncateToTwoDecimals(score).toFixed(2)}</span>
                      <div className={`text-xs font-normal ${colorClass}`}>{text}</div>
                  </div>
                  <svg className={`h-6 w-6 ml-6 ${colorClass}`} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 15V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V15M17 10L12 15M12 15L7 10M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
              </div>
          </div>
      </div>
    </div>
  )}

  return (
    <div className="w-full bg-white rounded-[24px] p-5 flex flex-col overflow-hidden">
        <div className="flex justify-between items-center">
            <div className="text-[#4A5568] text-2xl font-semibold">Análisis de variables</div>
            <a href="https://fsmsas.sharepoint.com/sites/FSM/Documentos%20compartidos/Forms/AllItems.aspx?csf=1&web=1&e=f3CZSu&CID=31f0b6ef%2Dbe67%2D45f8%2Dab7f%2D5ee9901a45e6&FolderCTID=0x012000E2E523EBE8E1EB4AA6D36045B168895C&id=%2Fsites%2FFSM%2FDocumentos%20compartidos%2FFSM%2FClientes%20Estudio1%2FFinMaq%20GO" target="_blank" rel="noreferrer" >
            <div className="bg-[#EDF2F7] rounded-[26px] px-4 py-2 flex items-center justify-center">
                <span className="text-[#390094] text-sm font-semibold">Ver carpeta de documentos</span>
            </div>
            </a>
        </div>

        {solicitud?.scoringResult && renderColorCodedScore()}

        <div className="flex-1 overflow-hidden">
            <StepperScoring
                formulario={solicitud || {}}
                setFormulario={setSolicitud}
                editable={fieldsEditable && !isReadyForStudy}
                onCompletionChange={handleCompletionChange}
                onCalculate={handleCalculate}
                isCalculating={isCalculating}
            />
        </div>

        {!fieldsEditable && solicitud?.scoringResult && !isReadyForStudy && (
            <div className="mt-4 flex justify-end">
                <button onClick={handleEditForm} className="w-[233px] h-[40px] border-[1px] border-[#390094]  rounded-3xl text-[#390094] text-md font-semibold hover:bg-[#390094] hover:text-white">
                    Editar formulario
                </button>
            </div>
        )}
    </div>
  )
}
